import type { SmartActionsInputProps } from '@bitte-ai/types';
import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from '../ui/textarea';
import { AgentPill } from './AgentPill';
import DefaultSendButton from './default-components/DefaultSendButtonComponent';

export const SmartActionsInput = ({
  input,
  isLoading,
  agentName,
  handleChange,
  handleSubmit,
  buttonColor,
  borderColor,
  textColor,
  backgroundColor,
  mobileInputExtraButton,
  customSendButtonComponent: SendButtonComponent = DefaultSendButton,
  placeholderText,
}: SmartActionsInputProps) => {
  const agentNameRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [paddingLeft, setPaddingLeft] = useState<number>(125);
  const [previousAgentName, setPreviousAgentName] =
    useState<string>('Select Agent');

  // Track the last input we received from parent to detect external changes
  const lastExternalInput = useRef(input);

  useEffect(() => {
    if (agentNameRef.current) {
      setPaddingLeft(agentNameRef.current.offsetWidth + 16);
    } else {
      setPaddingLeft(125);
    }
  }, [agentName]);

  useEffect(() => {
    if (agentName && agentName !== previousAgentName) {
      setPreviousAgentName(agentName);
    }
  }, [agentName]);

  // Only update textarea value if input changed externally (not from our own typing)
  useEffect(() => {
    if (textareaRef.current && input !== lastExternalInput.current) {
      // This is an external change (like clearing the input), so update the textarea
      const textarea = textareaRef.current;
      const currentCursor = textarea.selectionStart;

      textarea.value = input;
      lastExternalInput.current = input;

      // If the cursor position is still valid, restore it
      if (currentCursor <= input.length) {
        textarea.setSelectionRange(currentCursor, currentCursor);
      } else {
        // Position cursor at end if current position is beyond new text length
        textarea.setSelectionRange(input.length, input.length);
      }
    }
  }, [input]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Update our tracking reference
    lastExternalInput.current = e.target.value;
    // Notify parent - this won't cause a re-render that affects our cursor
    handleChange(e);
  };

  return (
    <form
      className="bitte-relative bitte-mb-0 bitte-flex bitte-w-full bitte-items-center bitte-justify-center bitte-gap-4 max-lg:bitte-flex-wrap"
      style={{ color: textColor }}
      onSubmit={handleSubmit}
    >
      <div className="bitte-w-full bitte-relative">
        <AgentPill name={agentName || previousAgentName} ref={agentNameRef} />

        <Textarea
          ref={textareaRef}
          placeholder={placeholderText || 'Message Smart Actions'}
          style={{
            paddingLeft: `${paddingLeft}px`,
            background: backgroundColor,
            borderColor: borderColor,
          }}
          className="bitte-h-[42px] bitte-w-full bitte-resize-none bitte-min-h-0 textarea-chat"
          onChange={handleTextareaChange}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
              e.preventDefault();
              handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
            }
          }}
          value={input}
        />
      </div>
      <div className="bitte-flex bitte-gap-2 bitte-w-full lg:bitte-contents">
        {mobileInputExtraButton ? (
          <div className="bitte-w-full lg:bitte-hidden">
            {mobileInputExtraButton}
          </div>
        ) : null}
        <SendButtonComponent
          input={lastExternalInput.current}
          isLoading={isLoading}
          buttonColor={buttonColor}
          textColor={textColor}
        />
      </div>
    </form>
  );
};
